<template>
    <div v-if="visible" class="modal-overlay">
        <div
            class="modal-content"
            role="dialog"
            aria-modal="true"
            aria-label="Informar valor numérico"
            @keydown.esc.stop.prevent="cancelar"
        >
            <h4 class="row-space">{{ opcoes.titulo }}</h4>

            <form @submit.prevent="onSubmit">
                <div class="row row-space">
                    <div class="form-item">
                        <label class="label-caption">
                            {{ opcoes.label }}

                            <input
                                ref="inputValor"
                                type="text"
                                v-model="edValor"
                                inputmode="decimal"
                                autocomplete="off"
                                class="input-right"
                                required
                                :aria-invalid="!!msgErro"
                                @input="msgErro = null"
                                @blur="formatarCampo"
                            />
                        </label>
                    </div>
                </div>

                <div v-if="msgErro" class="info-alert row-space" role="alert">
                    <span class="text">{{ msgErro }}</span>
                </div>

                <div class="row-center">
                    <button type="submit" class="btn-confirm">
                        Confirmar
                    </button>

                    <button
                        type="button"
                        class="btn-cancel"
                        @click="cancelar"
                    >
                        Cancelar
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>

<script setup>
import { ref, nextTick, onBeforeUnmount } from 'vue';

const visible = ref(false);
const inputValor = ref(null);
const edValor = ref('');
const msgErro = ref(null);
const opcoes = ref({});

let resolver = null;
let focoAnterior = null;

const formatador = new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: false,
});

function show(config = {}) {
    if (resolver) {
        throw new Error('O modal de valor já está aberto.');
    }

    opcoes.value = {
        titulo: 'Informar valor',
        label: 'Valor',
        valorInicial: null,
        minimo: null,
        maximo: null,
        ...config,
    };

    edValor.value = opcoes.value.valorInicial == null
        ? ''
        : formatador.format(opcoes.value.valorInicial);

    msgErro.value = null;
    focoAnterior = document.activeElement;
    visible.value = true;

    const retorno = new Promise((resolve) => {
        resolver = resolve;
    });

    nextTick(() => {
        inputValor.value?.focus();
        inputValor.value?.select();
    });

    return retorno;
}

function lerValor() {
    const texto = edValor.value.trim();

    if (!texto) {
        throw new Error('Informe um valor.');
    }

    // Aceita 1234,56 ou 1234.56, sem separador de milhar.
    if (!/^-?\d+(?:[.,]\d{1,2})?$/.test(texto)) {
        throw new Error(
            'Informe um número com até duas casas decimais, sem separador de milhar.'
        );
    }

    const valor = Number(texto.replace(',', '.'));

    if (!Number.isFinite(valor)) {
        throw new Error('Informe um valor válido.');
    }

    return valor;
}

function formatarCampo() {
    if (!edValor.value.trim()) return;

    try {
        edValor.value = formatador.format(lerValor());
    } catch {
        // Mantém o texto para o usuário corrigir.
    }
}

function onSubmit() {
    try {
        const valor = lerValor();

        if (opcoes.value.minimo != null && valor < opcoes.value.minimo) {
            throw new Error(
                `O valor mínimo é ${formatador.format(opcoes.value.minimo)}.`
            );
        }

        if (opcoes.value.maximo != null && valor > opcoes.value.maximo) {
            throw new Error(
                `O valor máximo é ${formatador.format(opcoes.value.maximo)}.`
            );
        }

        finalizar({ confirmado: true, valor });
    } catch (error) {
        msgErro.value = error.message;
        inputValor.value?.focus();
    }
}

function finalizar(resultado) {
    visible.value = false;

    const resolve = resolver;
    resolver = null;
    resolve?.(resultado);

    focoAnterior?.focus?.();
    focoAnterior = null;
}

function cancelar() {
    finalizar({ confirmado: false, valor: null });
}

onBeforeUnmount(() => {
    resolver?.({ confirmado: false, valor: null });
    resolver = null;
});

defineExpose({ show });
</script>

<style scoped>
.modal-overlay {
    position: fixed;
    inset: 0;
    z-index: 2000;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
}

.modal-content {
    box-sizing: border-box;
    background: white;
    padding: 20px;
    border-radius: 8px;
    width: 400px;
    max-width: calc(100vw - 32px);
    max-height: calc(100dvh - 32px);
    overflow-y: auto;
}
</style>